import querystring from 'querystring';
import { find, get, isNil, omitBy, pick, partition, sortBy } from 'lodash';
import { MARKETING_QUERY_PARAMS, TIMESGRID_QUERY_PARAMS } from '../../constants';
import { isProduction } from '../../utils/environment';
import flattenKeyValuePairArrayToObject from '../../Shared/utils/flattenKeyValuePairArrayToObject';
import { getSpoImpressionUrl, getSpoQueryParamsFromProviderLocationResult } from '../utils/spo';
import { isSeoUrl } from '../utils/isSeoUrl';

const CDN_REGEX = /(d2t808ag5aqhkh|d1cesmq0xhh7we|dsw5h1xg5uvx)\.cloudfront\.net/;

export function parseSearchGql({ data, ownProps }) {
    const {
        location,
        pageSize,
        parameters,
        theme,
    } = ownProps;

    const { pageOffset: page } = parameters;

    const searchRequestId = get(data, 'search.searchResponse.searchRequestId');
    const governmentInsuranceData = get(data, 'search.searchResponse.governmentInsuranceData');
    const showGovernmentInsuranceWarning = get(governmentInsuranceData, 'showGovernmentInsuranceWarning', false);

    const totalCount = showGovernmentInsuranceWarning ? 0 : get(data, 'search.searchResponse.totalCount', 0);

    const pageCount = Math.ceil(totalCount / pageSize);

    const { allowedStatuses } = theme.profile;

    const mergedProviderLocations = mergeProviderLocations({
        search: data.search,
        directoryId: theme.directoryId,
    });

    /**
     * We need to transform provider locations to a provider props
     * that search page understands.
     * TODO: refactor search page components to correctly accept data directly
     * from GQL
     */
    const oldSchemaProviders = mergedProviderLocations
        .filter(pl => filterProviderLocationByStatus(pl, allowedStatuses))
        .map(transformProviderLocation(ownProps, location));

    const doctorLocationIds = oldSchemaProviders
        .filter(pl => !pl.provider.nonBookable)
        .map(pl => {
            return `${pl.provider.monolithId}_${pl.location.monolithId}`;
        });

    const isInsuranceSearch = parseInt(parameters.planId, 10) > 0;
    const [previewProviderLocations, regularProviderLocations] = partition(oldSchemaProviders, isPreview);

    const {
        error,
        insuranceCarrierImage,
        loading,
        refetch,
    } = data;
    const {
        searchOrigin,
        searchResponse,
        seo,
        spo,
    } = data.search || {};
    const facets = get(searchResponse, 'facets', []);
    const visitReasons = getVisitReasonsFromFacets(facets);
    const telehealthAds = get(spo, 'telehealthAds', []);

    return {
        doctorLocationIds,
        error,
        facets,
        governmentInsuranceData,
        insuranceCarrierImage,
        loading,
        page,
        pageCount,
        providers: oldSchemaProviders,
        providersMappedByNetworkType: {
            inNetwork: inNetwork(isInsuranceSearch, regularProviderLocations),
            outOfNetwork: outOfNetwork(isInsuranceSearch, regularProviderLocations),
            nonInsurance: nonInsurance(isInsuranceSearch, regularProviderLocations),
            other: previewProviderLocations,
        },
        refetch,
        searchOrigin,
        searchRequestId,
        seo,
        shouldRenderReviewCount: shouldRenderReviewCount(oldSchemaProviders),
        telehealthAds,
        totalCount,
        visitReasons,
    };
}


function shouldRenderReviewCount(providers) {
    /*eslint-disable*/
    /*
    temporarily returning true regardless of isFeePerBooking for
    testing purposes. Will be reverted after testing finishes
    */
    return true;
    if (providers) {
        return providers.every(provider => get(provider, 'practice.isFeePerBooking'));
    } else {
        return false;
    }
}

function mergeProviderLocations({ search, directoryId }) {
    const spoProviderLocations = sortBy(get(search, 'spo.spoAds', []), 'adRank')
        .map(spoAd => ({
            ...spoAd.providerLocation,
            isSponsoredResult: true,
            sponsoredData: {
                adRank: spoAd.adRank,
                adDecisionId: spoAd.spoAdDecisionId,
                adDecisionToken: spoAd.adDecisionToken,
                adImpressionUrl: getSpoImpressionUrl(spoAd.spoAdDecisionId, spoAd.adDecisionToken, directoryId),
            },
        }));

    const providerLocations = get(search, 'searchResponse.providerLocations', []).slice();

    spoProviderLocations.forEach(pl => {
        const insertIndex = pl.sponsoredData.adRank - 1; // adRank is 1-index
        providerLocations.splice(insertIndex, 0, pl);
    });

    return providerLocations;
}

/**
 * Statuses:
 *
 * 1 New Application
 * 2 Approved Application
 * 4 Deleted
 * 5 Resource
 * 60 Locked
 * 61 Churned
 */
function filterProviderLocationByStatus(providerLocation, allowedStatuses) {
    const isAllowed = allowedStatuses.includes(providerLocation.provider.status);
    if (!isAllowed) {
        // eslint-disable-next-line
        console.error(`Search indicated that provider ${providerLocation.provider.id} was a viable search result, but its status is ${providerLocation.provider.status} and the allowed statuses are ${allowedStatuses}.`);
    }
    return isAllowed;
}

function transformProviderLocation(props, location) {
    return providerLocation => {
        const {
            parameters,
            isNewPatient,
        } = props;

        const { provider, practice } = providerLocation;

        let { profileUrl } = provider;

        // IMPORTANT: do not append url params on SEO pages so we do not lose traffic
        const shouldAppendQueryParams = !isSeoUrl(location.pathname);

        if (shouldAppendQueryParams) {
            const profileUrlParams = omitBy({
                LocIdent: providerLocation.location.monolithId,
                reason_visit: parameters.procedureId,
                insuranceCarrier: parameters.carrierId,
                insurancePlan: parameters.planId,
                dr_specialty: parameters.specialtyId,
                isNewPatient,
                ...getSpoQueryParamsFromProviderLocationResult(providerLocation),
                ...pick(location.query, [ // query params from React Router
                    ...MARKETING_QUERY_PARAMS,
                    ...TIMESGRID_QUERY_PARAMS,
                ]),
            }, isNil);

            const profileUrlString = querystring.stringify(profileUrlParams);
            profileUrl = `${provider.profileUrl}${profileUrlString ? `?${profileUrlString}` : ''}`;
        }

        const providerWithUrl = {
            ...provider,
            properties: flattenKeyValuePairArrayToObject(provider.properties),
            profileUrl,
        };

        const providerWithCorrectPictures = cleanPictureUrls(providerWithUrl, location);

        const finalOldSchemaProvider = {
            ...providerWithCorrectPictures,
            nonBookable: !provider.canHaveAppointments || !location,
            practice,
        };

        return {
            ...providerLocation,
            provider: finalOldSchemaProvider,
        };
    };
}

const getVisitReasonsFromFacets = facets => {
    const procedureFacet = find(facets, { facetId: 'procedures' });
    const visitReasons = get(procedureFacet, 'options', [])
        .map(option => ({
            id: option.optionId,
            name: option.displayName,
        }));
    return sortBy(visitReasons, x => x.name);
};

const cleanPictureUrls = (provider, stateLocation) => {
    if (provider.circlePictureUrl) {
        provider.circlePictureUrl = provider.circlePictureUrl.replace('mediumfixed', 'large');

        if (!isProduction(stateLocation.hostname)) {
            provider.circlePictureUrl = provider.circlePictureUrl.replace(CDN_REGEX, 'zocdoc.imgix.net');
        }
    }

    if (provider.squarePictureUrl) {
        provider.squarePictureUrl = provider.squarePictureUrl.replace('mediumfixed', 'large');

        if (!isProduction(stateLocation.hostname)) {
            provider.squarePictureUrl = provider.squarePictureUrl.replace(CDN_REGEX, 'zocdoc.imgix.net');
        }
    }
    return provider;
};

function isPreview(providerLocation) {
    return providerLocation.provider.isPreview;
}

function inNetwork(isInsuranceSearch, providerLocations) {
    return isInsuranceSearch
        ? providerLocations.filter(pl => pl.acceptsInsurance)
        : [];
}

function outOfNetwork(isInsuranceSearch, providerLocations) {
    return isInsuranceSearch
        ? providerLocations.filter(pl => !pl.acceptsInsurance)
        : [];
}

function nonInsurance(isInsuranceSearch, providerLocations) {
    return isInsuranceSearch
        ? []
        : providerLocations;
}
